/**
 * Sandbox-aware session storage.
 *
 * The "Try the full plan" demo runs in a per-tab `sessionStorage` scope so the
 * real logged-in session (in `localStorage`) is NEVER mutated while exploring a
 * demo. All token/session reads and writes route through this module.
 *
 * Resolution rule:
 *   - When `sandbox_active` is set (this tab is in a demo) → the sandbox keys in
 *     `sessionStorage`.
 *   - Otherwise (every real user, always) → the exact `localStorage` keys used
 *     before this feature existed — byte-identical behaviour.
 *
 * Because `sessionStorage` is per-tab and per-window:
 *   - a demo in tab A can't affect the real session another tab is using,
 *   - a reload mid-demo stays in the demo (sessionStorage survives reload),
 *   - and the real session, never having been touched, is simply "there" on exit.
 *
 * See also: api/constants/sandbox.mjs (BE side), AuthJwtProvider.enterSandbox/exitSandbox.
 *
 * SECOND CONSUMER — developer impersonation (api/scripts/impersonate.mjs) installs
 * a real couple's token into this same scope, because "an alternate session on this
 * tab only" is exactly what it needs too. So `sandbox_active` means "this tab is in
 * an alternate session", NOT "this tab is a demo event": do not add behaviour here
 * that assumes the session's event has `isSandbox: true`. Anything genuinely
 * demo-specific belongs behind the session user's `isSandbox` flag instead — which
 * an impersonation session deliberately does not set. (The two destructive sandbox
 * endpoints are already safe: `requireSandboxCouple` re-checks `isSandbox` on the
 * database record, so an impersonated real couple gets a 403.)
 */

// Real-session keys (localStorage) — unchanged from the original implementation.
const REAL_TOKEN = 'wedding_token';
const REAL_REFRESH = 'wedding_refresh_token';
const REAL_USER = 'session_user';

// Sandbox-session keys (sessionStorage) — per-tab, disposable.
const SANDBOX_ACTIVE = 'sandbox_active';
const SANDBOX_TOKEN = 'sandbox_token';
const SANDBOX_REFRESH = 'sandbox_refresh_token';
const SANDBOX_USER = 'sandbox_user';

/** True when this tab is currently exploring a sandbox demo. */
export function isSandboxActive(): boolean {
  try {
    return sessionStorage.getItem(SANDBOX_ACTIVE) === '1';
  } catch {
    return false;
  }
}

/** The storage object backing the active scope. */
function scopeStore(): Storage {
  return isSandboxActive() ? sessionStorage : localStorage;
}

/** The key set for the active scope. */
function scopeKeys() {
  return isSandboxActive()
    ? { token: SANDBOX_TOKEN, refresh: SANDBOX_REFRESH, user: SANDBOX_USER }
    : { token: REAL_TOKEN, refresh: REAL_REFRESH, user: REAL_USER };
}

// ── Token accessors (scope-aware) ──────────────────────────────────────────

export function getAccessToken(): string | null {
  try {
    return scopeStore().getItem(scopeKeys().token);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return scopeStore().getItem(scopeKeys().refresh);
  } catch {
    return null;
  }
}

export function setTokens(accessToken?: string, refreshToken?: string): void {
  const store = scopeStore();
  const keys = scopeKeys();
  if (accessToken) store.setItem(keys.token, accessToken);
  if (refreshToken) store.setItem(keys.refresh, refreshToken);
}

// ── Stored user JSON (scope-aware) ─────────────────────────────────────────

export function getStoredUserRaw(): string | null {
  try {
    return scopeStore().getItem(scopeKeys().user);
  } catch {
    return null;
  }
}

export function setStoredUserRaw(json: string): void {
  scopeStore().setItem(scopeKeys().user, json);
}

export function removeStoredUser(): void {
  scopeStore().removeItem(scopeKeys().user);
}

// ── Sandbox lifecycle ──────────────────────────────────────────────────────

/**
 * Enter a sandbox on THIS tab. Sets the active flag first so any subsequent
 * scope-aware write lands in the sandbox scope, then stores the demo session.
 * Never touches localStorage — the real session stays pristine.
 */
export function enterSandboxStorage(token: string, refreshToken: string, userJson: string): void {
  sessionStorage.setItem(SANDBOX_ACTIVE, '1');
  if (token) sessionStorage.setItem(SANDBOX_TOKEN, token);
  if (refreshToken) sessionStorage.setItem(SANDBOX_REFRESH, refreshToken);
  sessionStorage.setItem(SANDBOX_USER, userJson);
}

/** Clear the tab's sandbox scope (keys + active flag). Real session untouched. */
export function exitSandboxStorage(): void {
  try {
    sessionStorage.removeItem(SANDBOX_TOKEN);
    sessionStorage.removeItem(SANDBOX_REFRESH);
    sessionStorage.removeItem(SANDBOX_USER);
    sessionStorage.removeItem(SANDBOX_ACTIVE);
  } catch {
    /* ignore */
  }
}

// ── Real-session helpers ───────────────────────────────────────────────────

/** Remove the real session's token/refresh/user from localStorage. */
export function clearRealSession(): void {
  try {
    localStorage.removeItem(REAL_TOKEN);
    localStorage.removeItem(REAL_REFRESH);
    localStorage.removeItem(REAL_USER);
  } catch {
    /* ignore */
  }
}

/** Is there a real (non-sandbox) session persisted in localStorage? */
export function hasRealSession(): boolean {
  try {
    return !!localStorage.getItem(REAL_TOKEN) || !!localStorage.getItem(REAL_USER);
  } catch {
    return false;
  }
}

/** Raw real-session user JSON, regardless of the active scope (for exit re-hydration). */
export function getRealStoredUserRaw(): string | null {
  try {
    return localStorage.getItem(REAL_USER);
  } catch {
    return null;
  }
}
